import { useId, type ReactNode } from "react";
import {
  classNames,
  getFeedbackSemantics,
  joinIds,
  type FeedbackTone,
} from "./component-model";
import {
  buildMoveActionAccessibleLabel,
  canRequestMove,
  dispatchMoveRequest,
  type MoveAction,
} from "./move-controls-model";

export interface MoveControlsProps<ActionId extends string = string> {
  readonly id?: string;
  readonly itemLabel: string;
  readonly legend?: ReactNode;
  readonly instructions?: ReactNode;
  readonly currentPosition: ReactNode;
  readonly currentPositionLabel?: ReactNode;
  readonly actions: readonly MoveAction<ActionId>[];
  readonly children?: ReactNode;
  readonly busy?: boolean;
  readonly busyLabel?: ReactNode;
  readonly disabled?: boolean;
  readonly disabledReason?: ReactNode;
  readonly status?: ReactNode;
  readonly statusTone?: FeedbackTone;
  readonly live?: boolean;
  readonly className?: string;
  readonly onMoveRequest: (actionId: ActionId) => void;
}

export function MoveControls<ActionId extends string = string>({
  id,
  itemLabel,
  legend,
  instructions = "Use these controls to move this item without dragging.",
  currentPosition,
  currentPositionLabel = "Current position",
  actions,
  children,
  busy = false,
  busyLabel,
  disabled = false,
  disabledReason,
  status,
  statusTone = "info",
  live = true,
  className,
  onMoveRequest,
}: MoveControlsProps<ActionId>) {
  const generatedId = useId();
  const controlId = id ?? `wh-move-${generatedId}`;
  const instructionsId = `${controlId}-instructions`;
  const positionId = `${controlId}-position`;
  const statusId = `${controlId}-status`;
  const describedBy = joinIds(
    instructionsId,
    positionId,
    disabled || status ? statusId : undefined,
  );
  const announcement = busy
    ? (busyLabel ?? `Moving ${itemLabel}.`)
    : (status ??
      (disabled
        ? (disabledReason ?? `Moving ${itemLabel} is unavailable.`)
        : null));
  const announcementTone = busy
    ? "info"
    : status
      ? statusTone
      : disabled
        ? "warning"
        : statusTone;
  const announcementSemantics = getFeedbackSemantics(announcementTone, live);
  const requestState = { busy, disabled };

  return (
    <div
      className={classNames(
        "wh-move-controls",
        busy && "wh-move-controls--busy",
        disabled && "wh-move-controls--disabled",
        className,
      )}
    >
      <fieldset
        id={controlId}
        className="wh-move-controls__group"
        aria-describedby={describedBy}
        aria-busy={busy || undefined}
      >
        <legend className="wh-move-controls__legend">
          {legend ?? `Move ${itemLabel}`}
        </legend>
        <div className="wh-move-controls__content">
          <p className="wh-move-controls__instructions" id={instructionsId}>
            {instructions}
          </p>
          <p className="wh-move-controls__position" id={positionId}>
            <strong>{currentPositionLabel}:</strong> {currentPosition}
          </p>
          {children && (
            <div className="wh-move-controls__destination">{children}</div>
          )}
          <div className="wh-move-controls__actions">
            {actions.map((action, actionIndex) => {
              const available = canRequestMove(action, requestState);
              const actionReasonId = `${controlId}-action-${actionIndex}-reason`;
              const actionDescribedBy = joinIds(
                positionId,
                disabled || status ? statusId : undefined,
                action.disabled && action.disabledReason
                  ? actionReasonId
                  : undefined,
              );

              return (
                <div className="wh-move-controls__action" key={action.id}>
                  <button
                    type="button"
                    className="wh-move-controls__button"
                    data-move-action={action.id}
                    aria-label={buildMoveActionAccessibleLabel(
                      itemLabel,
                      action,
                    )}
                    aria-describedby={actionDescribedBy}
                    aria-disabled={!available || undefined}
                    onClick={() => {
                      dispatchMoveRequest(action, requestState, onMoveRequest);
                    }}
                  >
                    {action.label}
                  </button>
                  {action.disabled && action.disabledReason && (
                    <span
                      className="wh-move-controls__action-reason"
                      id={actionReasonId}
                    >
                      {action.disabledReason}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </fieldset>
      <p
        className="wh-move-controls__status"
        id={statusId}
        role={announcementSemantics.role}
        aria-live={announcementSemantics.liveMode}
        aria-atomic="true"
      >
        {announcement}
      </p>
    </div>
  );
}
